import { useRoleAuth } from '@src/hooks';
import { useCustomerNetAcquisitionLimits, useCustomers } from '@src/hooks/useCustomer';
import { useCustomerMarketAccounts } from '@src/providers';
import {
  ACTION,
  ButtonVariants,
  EMPTY_ARRAY,
  EntityAdmin,
  getRowNodesToOperateOn,
  IconName,
  useCallbackRef,
  useDisclosure,
  type ICustomerNetAcquisitionLimitMembership,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { CustomerStatusDialog } from './CustomerStatusDialog';
import {
  ACQUISITION_LIMIT_MEMBERSHIP_COLUMNS,
  getAcquisitionLimitMembershipAsDrawerEntity,
  getAcquisitionLimitMembershipDrawerOptions,
  viewStatusMenuItem,
  type AcquisitionLimitMembershipDrawerEntity,
} from './utils';

// By convention we don't include market account in request paths
const path = '/customer-risk/net-acquisition/memberships';
const getUpdatePath = () => path;

export function CustomerAcquisitionLimitMemberships() {
  const customers = useCustomers();
  const { customerMarketAccountsList, customerMarketAccountsByName } = useCustomerMarketAccounts();
  const acquisitionLimits = useCustomerNetAcquisitionLimits({ tag: 'CustomerAcquisitionLimitMemberships' });
  const { isAuthorized } = useRoleAuth();

  const allowEdit = isAuthorized(ACTION.EDIT_CUSTOMER_ACQUISITION_LIMITS);
  const drawerOptions = useMemo(() => {
    if (customers != null && customerMarketAccountsList != null && acquisitionLimits != null) {
      return getAcquisitionLimitMembershipDrawerOptions(customers, customerMarketAccountsList, acquisitionLimits);
    }
  }, [customerMarketAccountsList, customers, acquisitionLimits]);

  const [selectedMktAcnt, setSelectedMktAcnt] = useState<string>();

  const statusDialog = useDisclosure();
  const statusDialogAction = useMemo(
    () => ({
      label: 'Look Up Customer',
      onClick: () => {
        setSelectedMktAcnt(undefined);
        statusDialog.open();
      },
      variant: ButtonVariants.Priority,
      startIcon: IconName.User,
    }),
    [statusDialog]
  );

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => {
      const selectedItems: ICustomerNetAcquisitionLimitMembership[] = getRowNodesToOperateOn(params).map(
        node => node.data
      );
      if (selectedItems.length !== 1) {
        return [];
      }

      return [
        viewStatusMenuItem(() => {
          setSelectedMktAcnt(selectedItems[0].MarketAccount);
          statusDialog.open();
        }),
      ];
    },
    [statusDialog, setSelectedMktAcnt]
  );

  const getMembershipAsDrawerEntity = useCallbackRef((entity: ICustomerNetAcquisitionLimitMembership) =>
    getAcquisitionLimitMembershipAsDrawerEntity(entity, customerMarketAccountsByName)
  );

  const getDeletePath = useCallback(
    (entity: AcquisitionLimitMembershipDrawerEntity) => `${path}?MarketAccounts=${entity.MarketAccount}`,
    []
  );

  return (
    <>
      <CustomerStatusDialog preselectedMarketAccount={selectedMktAcnt} {...statusDialog} />
      <EntityAdmin<ICustomerNetAcquisitionLimitMembership, AcquisitionLimitMembershipDrawerEntity>
        title="Customer Net Acquisition Limit Memberships"
        subtitle="Assign Customers to Net Acquisition Limits"
        entityName="Net Acquisition Membership"
        path={path}
        getPatchPath={getUpdatePath}
        getDeletePath={getDeletePath}
        sendEmptyStringForNulledValues={true}
        entityIDField="MarketAccount"
        columns={ACQUISITION_LIMIT_MEMBERSHIP_COLUMNS}
        panelActions={[statusDialogAction]}
        drawerOptions={drawerOptions}
        filterableProperties={EMPTY_ARRAY}
        allowAddEntity={allowEdit}
        allowEditEntity={allowEdit}
        allowDeleteEntity={allowEdit}
        getEntityAsDrawerEntity={getMembershipAsDrawerEntity}
        getCustomContextMenuItems={getCustomContextMenuItems}
        persistKey="dealer-customer-net-acquisition-memberships"
      />
    </>
  );
}
